import { useCallback, useMemo } from 'react';
import {
  findActiveTabIdsFromPath,
  getTabRows,
  resolveTabClickPath,
} from './detailUseCase';
import { buildDetailNavUrl } from '../../utils/url';

/**
 * @param {object} params
 * @param {DetailTabItem[]} params.tabs
 * @param {string} params.currentPath - Current location (pathname + search)
 * @param {URLSearchParams} params.searchParams
 * @param {import('react-router-dom').NavigateFunction} params.navigate
 * @returns {{
 *   tabRows: DetailTabItem[][],
 *   activeTabIds: string[],
 *   handleTabClickAtLevel: (level: number, tabId: string) => void,
 *   getTabHref: (level: number, tabId: string) => string | undefined,
 * }}
 */
const useDetailTabs = ({
  tabs,
  currentPath,
  searchParams,
  navigate,
}) => {
  const activeTabIds = useMemo(
    () => findActiveTabIdsFromPath(currentPath, tabs),
    [currentPath, tabs],
  );

  const tabRows = useMemo(
    () => getTabRows(tabs, activeTabIds),
    [tabs, activeTabIds],
  );

  const handleTabClickAtLevel = useCallback((level, tabId) => {
    const targetPath = resolveTabClickPath(tabs, activeTabIds, level, tabId);
    if (!targetPath) return;

    navigate(buildDetailNavUrl(targetPath, searchParams));
  }, [tabs, activeTabIds, searchParams, navigate]);

  const getTabHref = useCallback((level, tabId) => {
    const targetPath = resolveTabClickPath(tabs, activeTabIds, level, tabId);
    if (!targetPath) return undefined;
    return buildDetailNavUrl(targetPath, searchParams);
  }, [tabs, activeTabIds, searchParams]);

  return {
    tabRows,
    activeTabIds,
    handleTabClickAtLevel,
    getTabHref,
  };
};

export default useDetailTabs;
